import React from 'react'
// Gov.br Design System React Components
import {
    BrButton,
    BrDivider,
    BrTag,
    BrFooter
} from '@govbr-ds/react-components'

interface UIComponent {
    type: string
    props?: Record<string, unknown>
    content?: string
    children?: UIComponent[]
    accessibility?: Record<string, string>
}

interface UISpec {
    components: UIComponent[]
    layout?: {
        type?: string
        maxWidth?: string
        padding?: string
        gap?: string
    }
    content?: {
        title?: string
        description?: string
        labels?: Record<string, string>
    }
    accessibility?: {
        highContrast?: boolean
        fontSize?: string
        reducedMotion?: boolean
        screenReaderOptimized?: boolean
    }
    theme?: {
        colorScheme?: string
    }
}

interface DynamicUIRendererProps {
    uiSpec: UISpec | string | object
    className?: string
    fullscreen?: boolean
    accessibilityNeeds?: string  // e.g., 'low_vision', 'photophobia', 'low_vision_and_photophobia'
}

/**
 * Renders a Gov.br component based on the ui_spec JSON structure.
 * This is a simplified renderer for the POC that converts the LLM-generated
 * UI specifications into visual React components.
 */
function renderComponent(component: UIComponent, index: number): React.ReactNode {
    const { type, props = {}, content, children, accessibility } = component

    // Common classes based on component type
    // Using Gov.br Design System native classes
    const baseClasses: Record<string, string> = {
        // Headers and panels use Gov.br primary blue with pure white text
        'br-header': 'bg-primary-default text-pure-0 p-3 rounded-top',
        'br-panel': 'bg-primary-default text-pure-0 p-3 rounded',
        'br-section': 'bg-primary-default text-pure-0 p-3 rounded',
        'br-footer': 'bg-primary-darken-02 text-pure-0 p-3 rounded-bottom small',
        'br-card': 'bg-pure-0 border rounded p-3 shadow-sm',
        'br-button': 'px-3 py-2 rounded fw-medium',
        'br-message': 'p-3 rounded border-start border-4',
        'br-alert': 'p-3 rounded border',
        'br-text': 'text-gray-80',
        'br-list': 'list-disc ps-3',
        'br-list-item': 'py-1 text-gray-80 d-flex align-items-start gap-2',
        'br-breadcrumb': 'small text-gray-60 d-flex align-items-center gap-2',
        'br-container': 'container',
        'br-row': 'd-flex flex-wrap gap-2',
        'br-col': 'flex-fill',
        'br-divider': 'border-top my-3',
        'br-tag': 'd-inline-block px-2 py-1 small fw-medium rounded',
        'br-table': 'w-100 table',
        'br-input': 'w-100 p-2 border rounded',
        'br-loading': 'spinner-border',
        'br-help': 'd-inline-flex align-items-center gap-1 small text-primary-default',
        'br-tooltip': 'position-relative',
        'br-icon': 'd-inline-block',
        'br-link': 'text-primary-default text-decoration-none',
    }

    // Get variant/state-specific classes (Bootstrap 5)
    const getVariantClasses = (): string => {
        const variant = props.variant as string
        const state = props.state as string

        if (type === 'br-button') {
            switch (variant) {
                case 'primary': return 'btn btn-primary'
                case 'secondary': return 'btn btn-outline-primary'
                case 'tertiary': return 'btn btn-link'
                default: return 'btn btn-primary'
            }
        }

        // br-message and br-alert: classes applied in the render case using native Gov.br CSS
        if (type === 'br-message' || type === 'br-alert') {
            return '' // Empty - native br-message classes applied in render case
        }

        if (type === 'br-tag') {
            switch (state || variant) {
                case 'success': return 'bg-success-subtle text-success'
                case 'warning': return 'bg-warning-subtle text-warning'
                case 'error': case 'danger': return 'bg-danger-subtle text-danger'
                default: return 'bg-secondary-subtle text-secondary'
            }
        }

        return ''
    }

    const className = `${baseClasses[type] || ''} ${getVariantClasses()} ${(props.className as string) || ''}`.trim()
    const ariaLabel = accessibility?.['aria-label'] || accessibility?.ariaLabel

    // Render children recursively
    const renderedChildren = children?.map((child, i) => renderComponent(child, i))

    switch (type) {
        case 'br-header':
            // Gov.br header with primary blue background and pure white text
            // The h1 element inside is naturally navigable by VoiceOver headings mode
            return (
                <header
                    key={index}
                    className="bg-primary-default text-pure-0 p-3 rounded-top"
                    aria-label={ariaLabel}
                >
                    {typeof props.title === 'string' ? <h1 tabIndex={0} className="h5 fw-bold text-pure-0 mb-1 text-break">{props.title}</h1> : null}
                    {typeof props.subtitle === 'string' ? <p className="small text-pure-0 opacity-75 mb-0">{props.subtitle}</p> : null}
                    {content ? <span className="text-pure-0">{content}</span> : null}
                    {renderedChildren}
                </header>
            )

        case 'br-footer':
            // Footer is added automatically at the end by DynamicUIRenderer
            // Skip any br-footer from LLM to avoid duplicates
            return null

        case 'br-card': {
            // Handle content that can be string or object with title
            const cardContent = typeof content === 'object' && content !== null
                ? (content as { title?: string }).title
                : content
            const cardTitle = typeof props.title === 'string'
                ? props.title
                : (typeof content === 'object' && content !== null ? (content as { title?: string }).title : undefined)

            // Filter out children that duplicate the title (LLM sometimes generates br-text with same title)
            const filteredChildren = children?.filter((child, i) => {
                if (child.type === 'br-text' && i === 0) {
                    const childContent = child.content
                    // If first child is br-text with same content as title, skip it
                    if (typeof childContent === 'string' && cardTitle && childContent.trim() === cardTitle.trim()) {
                        return false
                    }
                }
                return true
            })

            const filteredRenderedChildren = filteredChildren?.map((child, i) => renderComponent(child, i))

            // Generate unique IDs for accessibility
            const cardTitleId = cardTitle ? `card-title-${index}` : undefined
            const cardContentId = `card-content-${index}`

            // Card styling with accessibility:
            // - role="region" makes it a landmark for VoiceOver navigation
            // - tabIndex={0} makes it focusable so VoiceOver can land on it
            // - aria-labelledby announces the card title when focused
            return (
                <section
                    key={index}
                    role="region"
                    tabIndex={0}
                    className={`br-card border rounded p-1 shadow-sm w-100 ${(props.className as string) || ''}`}
                    style={props.style as React.CSSProperties}
                    aria-labelledby={cardTitleId}
                    aria-describedby={cardContentId}
                >
                    {cardTitle ? (
                        <h3 id={cardTitleId} className="h6 fw-semibold mb-2 text-break">{cardTitle}</h3>
                    ) : null}
                    <div id={cardContentId}>
                        {typeof cardContent === 'string' && cardContent !== cardTitle ? <p className="mb-0">{cardContent}</p> : null}
                        {filteredRenderedChildren}
                    </div>
                </section>
            )
        }

        case 'br-button': {
            const isPrimary = props.variant === 'primary' || !props.variant
            const isSecondary = props.variant === 'secondary'
            const isTertiary = props.variant === 'tertiary'

            // Extract button text from multiple possible sources
            // LLM may put text in different places
            let buttonText = ''
            if (typeof props.text === 'string' && props.text.trim()) {
                buttonText = props.text.trim()
            } else if (typeof props.label === 'string' && props.label.trim()) {
                buttonText = props.label.trim()
            } else if (typeof content === 'string' && content.trim()) {
                buttonText = content.trim()
            } else if (children && children.length > 0) {
                // Try to get text from first child
                const firstChild = children[0]
                if (typeof firstChild.content === 'string') {
                    buttonText = firstChild.content.trim()
                }
            }

            // Final fallback
            if (!buttonText) buttonText = 'Ação'

            // Use BrButton with explicit aria-label for VoiceOver support
            return (
                <BrButton
                    key={index}
                    primary={isPrimary && !isSecondary}
                    secondary={isSecondary || isTertiary}
                    type="button"
                    aria-label={buttonText}
                    className="mr-2 mb-2 mt-3 text-break w-100"
                >
                    {buttonText}
                </BrButton>
            )
        }

        case 'br-message': {
            // Get status from props - Gov.br uses: info, success, warning, danger
            const status = (props.state as string) || (props.status as string) || 'info'
            const messageContent = content || props.message as string || ''
            const hasTitle = typeof props.title === 'string' && (props.title as string).trim().length > 0
            const hasContent = typeof messageContent === 'string' && messageContent.trim().length > 0
            const hasChildren = renderedChildren && (Array.isArray(renderedChildren) ? renderedChildren.length > 0 : true)

            // Use native Gov.br DS classes for message component
            const statusClass = `br-message ${status}`

            // aria-live for screen reader announcements
            const isUrgent = status === 'danger' || status === 'warning'

            return (
                <div
                    key={index}
                    className={`${statusClass} w-full mb-3`}
                    role="alert"
                    aria-live={isUrgent ? 'assertive' : 'polite'}
                    aria-atomic="true"
                >
                    <div className="content">
                        {hasTitle && (
                            <span className="message-title fw-semibold d-block mb-1">
                                {props.title as string}
                            </span>
                        )}
                        {hasContent && <span className="message-body">{messageContent}</span>}
                        {hasChildren && <div className="mt-2">{renderedChildren}</div>}
                    </div>
                </div>
            )
        }

        case 'br-alert': {
            // Ensure margin between alert and following elements
            const alertContent = String(content || props.message || '')
            const alertTitle = props.title as string
            return (
                <div key={index} className={`${className} mb-3`} role="alert">
                    {alertTitle && <strong className="d-block mb-1">{alertTitle}</strong>}
                    {alertContent}
                    {renderedChildren}
                </div>
            )
        }

        case 'br-text': {
            // Support both props.tag (LLM generates this) and props.variant
            const tag = props.tag as string || props.variant as string
            const validTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'li', 'div']
            const TextTag = (validTags.includes(tag) ? tag : 'p') as keyof JSX.IntrinsicElements

            // Map large headings to smaller font sizes using Bootstrap fs-* classes
            // This prevents overflow at high zoom while keeping semantic structure
            const fontSizeMap: Record<string, string> = {
                'h1': 'fs-5', // h1 renders at h5 size
                'h2': 'fs-5', // h2 renders at h5 size  
                'h3': 'fs-6', // h3 renders at h6 size
            }
            const fontSizeClass = fontSizeMap[tag] || ''

            // Check if content contains HTML tags - if so, render as HTML
            const hasHtml = typeof content === 'string' && /<[a-z][\s\S]*>/i.test(content)

            if (hasHtml && typeof content === 'string') {
                return (
                    <TextTag
                        key={index}
                        className={`${className} ${fontSizeClass} text-break mb-3`}
                        dangerouslySetInnerHTML={{ __html: content }}
                    />
                )
            }

            // Add text-break, font size limiting, and margin-bottom for spacing between text elements
            const textClass = `${className} ${fontSizeClass} text-break mb-3`.trim()

            return (
                <TextTag key={index} className={textClass}>
                    {content || renderedChildren}
                </TextTag>
            )
        }

        case 'br-list': {
            // Support items from both props.items AND content.items (LLM generates both structures)
            const contentObj = typeof content === 'object' && content !== null
                ? content as { items?: Array<string | { title?: string; description?: string }> }
                : null

            // Smart text parsing: LLM sometimes outputs items as text separated by double spaces or sentence patterns
            let parsedItems: Array<string | { title?: string; description?: string }> | undefined

            if (typeof content === 'string' && content.trim().length > 0) {
                // Try to split by double space, newlines, or common sentence patterns
                const textContent = content.trim()

                // Pattern 1: Multiple sentences separated by double spaces
                if (textContent.includes('   ')) {
                    parsedItems = textContent.split(/\s{3,}/).filter(s => s.trim().length > 0)
                }
                // Pattern 2: Sentences that look like list items (starts with capital, ends with period/lowercase before next capital)
                else if (/[.!?]\s+[A-Z]/.test(textContent)) {
                    parsedItems = textContent.split(/(?<=[.!?])\s+(?=[A-Z])/).filter(s => s.trim().length > 0)
                }
            }

            const rawItems = (props.items || contentObj?.items || parsedItems) as Array<string | { title?: string; description?: string }> | undefined

            // Filter out empty/null items
            const filteredItems = rawItems?.filter(item => {
                if (typeof item === 'string') {
                    return item.trim().length > 0
                }
                if (typeof item === 'object' && item !== null) {
                    return (item.title && item.title.trim().length > 0) || (item.description && item.description.trim().length > 0)
                }
                return false
            })

            // If no items after filtering, return null
            if (!filteredItems || filteredItems.length === 0) {
                return null
            }

            // Use list title as heading above the list, not aria-label
            const listTitle = typeof props.title === 'string' ? props.title : undefined

            return (
                <div key={index} className={`${className} mb-3`}>
                    {listTitle && <h4 className="h6 fw-semibold mb-2">{listTitle}</h4>}
                    <ul style={{ listStyleType: 'disc', paddingLeft: '1.5rem', margin: 0 }}>
                        {filteredItems.map((item, i) => (
                            <li key={i} className="py-1">
                                {typeof item === 'string'
                                    ? item
                                    : <>
                                        {item.title && <span className="fw-medium">{item.title}</span>}
                                        {item.description && <span className="text-gray-60"> {item.description}</span>}
                                    </>
                                }
                            </li>
                        ))}
                    </ul>
                    {renderedChildren}
                </div>
            )
        }
        case 'br-breadcrumb': {
            const links = props.links as Array<{ label: string; url?: string; active?: boolean }> | undefined
            return (
                <nav key={index} className={className} aria-label="Navegação estrutural">
                    {links?.map((link, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && <span className="text-gray-60">/</span>}
                            {link.active ? (
                                <span className="text-gray-80 fw-medium">{link.label}</span>
                            ) : (
                                <a href={link.url || '#'} className="text-decoration-underline">{link.label}</a>
                            )}
                        </React.Fragment>
                    ))}
                    {content}
                </nav>
            )
        }

        case 'br-container':
            return (
                <div key={index} className={className}>
                    {renderedChildren}
                </div>
            )

        case 'br-row':
            return (
                <div key={index} className={className}>
                    {renderedChildren}
                </div>
            )

        case 'br-col':
            return (
                <div key={index} className={className}>
                    {renderedChildren}
                </div>
            )

        case 'br-divider':
            return <BrDivider key={index} />

        case 'br-tag': {
            // Use content, or props.text (LLM often uses this), or props.label
            const tagValue = content || String(props.text || props.label || '')
            // Skip rendering if no value
            if (!tagValue.trim()) return null
            return (
                <div key={index} className="mb-2 mt-2">
                    <BrTag
                        type="status"
                        value={tagValue}
                    />
                </div>
            )
        }

        case 'br-input':
            return (
                <input
                    key={index}
                    className={className}
                    type={(props.type as string) || 'text'}
                    placeholder={props.placeholder as string}
                    aria-label={ariaLabel}
                />
            )

        case 'br-loading':
            return <div key={index} className={className} aria-label="Carregando" />

        case 'br-help':
            // Only render if there's actual content to show
            if (!content) return null
            return (
                <div key={index} className="d-flex align-items-start gap-2 text-gray-80 mb-3">
                    <i className="far fa-question-circle text-primary-default flex-shrink-0" style={{ marginTop: '0.2em' }} aria-hidden="true"></i>
                    <span>{content}</span>
                </div>
            )

        case 'br-link':
            return (
                <a
                    key={index}
                    href={(props.href as string) || '#'}
                    className={`${className} d-block mb-2 text-primary-default`}
                >
                    {content || String(props.label || '')}
                </a>
            )

        case 'br-table': {
            // Support both children-based structure AND content.data structure
            const tableContent = component.content as { data?: Array<Record<string, string>>, title?: string } | string | undefined
            const hasContentData = typeof tableContent === 'object' && tableContent?.data && tableContent.data.length > 0
            const hasChildren = children && children.length > 0

            // If no data at all, show empty state
            if (!hasChildren && !hasContentData) {
                return (
                    <div key={index} className={`${className} small text-secondary fst-italic`}>
                        Sem dados disponíveis no momento
                    </div>
                )
            }

            // If content.data exists (LLM generates this structure), render from data
            if (hasContentData && typeof tableContent === 'object' && tableContent.data) {
                const columns = props.columns as Array<{ header: string; field: string }> | undefined

                // Find date field and sort by it (descending - most recent first)
                const dateFields = ['date', 'data', 'Data', 'DATE']
                const rawData = [...tableContent.data]

                // Try to find a date field and sort
                const dateField = dateFields.find(f => rawData[0] && f in rawData[0])
                if (dateField) {
                    rawData.sort((a, b) => {
                        const dateA = a[dateField] || ''
                        const dateB = b[dateField] || ''
                        // Parse dd/mm/yyyy format
                        const parseDate = (d: string) => {
                            const parts = d.split('/')
                            if (parts.length === 3) {
                                return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0])).getTime()
                            }
                            return 0
                        }
                        return parseDate(dateB) - parseDate(dateA) // Descending
                    })
                } else {
                    // Fallback: just reverse order
                    rawData.reverse()
                }
                const data = rawData

                // Infer columns from first data item if not provided
                const inferredColumns = columns || Object.keys(tableContent.data[0] || {}).map(key => ({
                    header: key.charAt(0).toUpperCase() + key.slice(1),
                    field: key
                }))

                return (
                    <div key={index} className="w-100">
                        <table className={`${className} w-full border-collapse border border-gray-20`}>
                            <thead className="bg-gray-2">
                                <tr>
                                    {inferredColumns.map((col, ci) => (
                                        <th key={ci} className="p-2 text-left font-semibold text-gray-80 border-b border-gray-20">
                                            {col.header}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((row, ri) => (
                                    <tr key={ri} className="border-b border-gray-10 last:border-b-0">
                                        {inferredColumns.map((col, ci) => (
                                            <td key={ci} className="p-2 text-gray-80">
                                                {row[col.field] || ''}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            }

            // Otherwise, use children-based structure
            // Check if first row has th elements (header row)
            const hasHeader = children![0]?.type === 'br-table-row' &&
                children![0]?.children?.some((c: UIComponent) => c.type === 'br-th' || c.type === 'br-table-header')

            // Reverse data rows to show most recent first (keep header at top)
            const dataRows = hasHeader ? children!.slice(1).reverse() : (children || []).slice().reverse()

            return (
                <div key={index} className="w-100">
                    <table className={`${className} w-full border-collapse border border-gray-20`}>
                        {hasHeader && children![0] ? (
                            <>
                                <thead className="bg-gray-2">
                                    {renderComponent(children![0], 0)}
                                </thead>
                                <tbody>
                                    {dataRows.map((child: UIComponent, i: number) => renderComponent(child, i + 1))}
                                </tbody>
                            </>
                        ) : (
                            <tbody>
                                {dataRows.map((child: UIComponent, i: number) => renderComponent(child, i))}
                            </tbody>
                        )}
                    </table>
                </div>
            )
        }

        case 'br-table-row':
        case 'br-tr':
            return <tr key={index} className="border-b border-gray-10 last:border-b-0">{renderedChildren}</tr>

        case 'br-table-header':
        case 'br-th':
            return (
                <th key={index} className="p-2 bg-gray-10 text-left font-semibold text-gray-80">
                    {content || renderedChildren}
                </th>
            )

        case 'br-table-cell':
        case 'br-td':
            return (
                <td key={index} className="p-2 border-l border-gray-10 first:border-l-0">
                    {content || renderedChildren}
                </td>
            )

        // Handle br-tooltip - render content with hover tooltip
        case 'br-tooltip': {
            const tooltipText = (props.text as string) || ''
            return (
                <li key={index} className="py-1 text-gray-800 list-disc ml-4" title={tooltipText}>
                    <span className="cursor-help border-b border-dotted border-gray-400">
                        {content || renderedChildren}
                    </span>
                </li>
            )
        }

        // Handle br-list-item explicitly
        case 'br-list-item':
            return (
                <li key={index} className="py-1 text-gray-800">
                    {content || renderedChildren}
                </li>
            )

        default:
            // Generic fallback for unknown components - render content without metadata
            return (
                <div key={index} className={className || 'text-gray-800'}>
                    {content && <span>{content}</span>}
                    {renderedChildren}
                </div>
            )
    }
}

export function DynamicUIRenderer({ uiSpec, className = '', fullscreen = false, accessibilityNeeds = '' }: DynamicUIRendererProps) {
    // Parse JSON string if needed
    let spec: UISpec
    try {
        spec = typeof uiSpec === 'string' ? JSON.parse(uiSpec) : uiSpec
    } catch (_e) {
        return (
            <div className="p-4 bg-danger border border-danger rounded-lg text-danger">
                <p className="font-medium">Erro ao processar especificação da interface</p>
                <p className="text-sm mt-1">O JSON da UI não pôde ser parseado.</p>
            </div>
        )
    }

    if (!spec || !spec.components || spec.components.length === 0) {
        return (
            <div className="p-4 bg-gray-2 border border-gray-20 rounded-lg text-gray-60 text-center">
                <p>Nenhuma especificação de interface disponível</p>
            </div>
        )
    }

    // Apply accessibility settings
    const accessibilityClasses: string[] = []

    // Apply accessible typography for high contrast mode (low vision users like John Smith)
    if (spec.accessibility?.highContrast) {
        accessibilityClasses.push('high-contrast')
        accessibilityClasses.push('accessible-typography')
    }

    // Apply larger font sizes
    if (spec.accessibility?.fontSize === 'large') {
        accessibilityClasses.push('text-lg')
        accessibilityClasses.push('accessible-typography')
    } else if (spec.accessibility?.fontSize === 'x-large') {
        accessibilityClasses.push('text-xl')
        accessibilityClasses.push('accessible-typography')
    }

    // Theme is determined by LLM-generated components
    // Apply dark mode when colorScheme is 'dark' (for photophobia users like Dra. Emily)
    const isDarkMode = spec.theme?.colorScheme === 'dark'

    const themeClasses = [
        spec.accessibility?.highContrast ? 'high-contrast' : '',
        isDarkMode ? 'dark-mode' : ''
    ].filter(Boolean).join(' ')

    // Layout settings - adjust based on fullscreen mode (using Gov.br/Bootstrap scale)
    const layoutStyle: React.CSSProperties = fullscreen
        ? { padding: '8px' }  // Compact for fullscreen
        : { padding: spec.layout?.padding === 'large' ? '32px' : spec.layout?.padding === 'medium' ? '16px' : '8px' }

    // Container classes using Bootstrap 5 (included in Gov.br DS)
    // Fullscreen: w-100 (full width), px-2 (8px padding)
    // Normal: container (max-width + auto margins), px-3 (16px padding)
    const containerClasses = fullscreen
        ? 'w-100 px-2'
        : 'container px-3'

    // Check if low vision zoom is needed
    const hasLowVision = accessibilityNeeds.includes('low_vision')

    // Apply 200% zoom via inline style for low_vision (32px = 200% of 16px)
    const zoomStyle: React.CSSProperties = hasLowVision ? {
        fontSize: '32px',
        lineHeight: '1.4'
    } : {}

    return (
        <div
            className={`dynamic-ui-preview ${themeClasses} ${accessibilityClasses.join(' ')} ${fullscreen ? 'fullscreen-mode' : ''} ${hasLowVision ? 'low-vision-zoom' : ''} ${className}`}
            style={zoomStyle}
        >
            {/* Main content area */}
            <div className={containerClasses} style={layoutStyle}>
                {/* Title from content if exists */}
                {spec.content?.title && (
                    <div className={fullscreen ? 'mb-2 pb-1 border-bottom' : 'mb-3 pb-2 border-bottom'}>
                        <h2 className={fullscreen ? 'h6 fw-semibold text-gray-80' : 'h5 fw-semibold text-gray-80'}>{spec.content.title}</h2>
                        {spec.content.description && (
                            <p className="small text-gray-60 mt-1">{spec.content.description}</p>
                        )}
                    </div>
                )}

                {/* Render all components with Bootstrap spacing */}
                <div className={fullscreen ? 'fullscreen-components' : 'normal-components'}>
                    {spec.components.map((component, index) => renderComponent(component, index))}
                </div>
            </div>

            {/* Gov.br BrFooter - hidden from screen readers to avoid redundant reading */}
            <div aria-hidden="true">
                <BrFooter
                    urlLogo="/images/logo-tst.svg"
                    socialNetworks={[
                        { icon: 'fab fa-youtube', link: 'https://www.youtube.com/tvtst', name: 'YouTube' },
                        { icon: 'fab fa-instagram', link: 'https://www.instagram.com/tsaborista', name: 'Instagram' },
                        { icon: 'fab fa-facebook-f', link: 'https://www.facebook.com/TribunalSuperior', name: 'Facebook' },
                        { icon: 'fab fa-twitter', link: 'https://twitter.com/TST_oficial', name: 'Twitter' }
                    ]}
                    userLicenseText={
                        <span>© {new Date().getFullYear()} Tribunal Superior do Trabalho - Todos os direitos reservados</span>
                    }
                    inverted
                />
            </div>
        </div>
    )
}

export default DynamicUIRenderer
